import React, { forwardRef, HTMLAttributes, PropsWithChildren } from 'react'
import { Menu as MenuStyles } from '@airbus/elements/elements'
import clsx from 'clsx'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import { createUseStyles } from '../../lib/styling'
import Divider from '../Divider'
import { CommonProps, PolymorphicPropsWithRef, PolymorphicRef } from '../helperTypes'

const useStyles = createUseStyles(MenuStyles, 'list')

type Props = {
  /** Disabled state */
  disabled?: boolean
  /** onClick handler */
  onClick?: React.MouseEventHandler<any>
  /** onKeyDown handler */
  onKeyDown?: React.KeyboardEventHandler<any>
  /** If true, selected state will be background less */
  quiet?: boolean
  /** Selected state */
  selected?: boolean
}

export type ListItemProps<C extends React.ElementType> = PolymorphicPropsWithRef<C, Props & CommonProps>

// type annotation for strong typing of polymorphic ref
type PolymorphicListItem = <C extends React.ElementType = 'li'>(props: ListItemProps<C>) => React.ReactElement | null

export const ListItem: PolymorphicListItem = forwardRef(
  <C extends React.ElementType>(props: ListItemProps<C>, ref?: PolymorphicRef<C>) => {
    const {
      children,
      className,
      component,
      disabled = false,
      quiet = false,
      onClick,
      onKeyDown,
      selected = false,
      ...rest
    } = props
    const ComponentProp = component || 'li'
    const classes = useStyles()

    return (
      <ComponentProp
        ref={ref}
        disabled={disabled ? true : undefined}
        aria-disabled={disabled}
        className={clsx(
          className,
          classes.item,
          disabled && classes.item_disabled,
          quiet && classes.item_quiet,
          selected && classes.item_selected,
        )}
        onKeyDown={(e: React.KeyboardEvent) => {
          const isSpaceOrEnter = e.key === ' ' || e.key === 'Enter'
          if (disabled && isSpaceOrEnter) {
            e.stopPropagation()
            return
          }
          if (onKeyDown) {
            e.preventDefault()
            onKeyDown(e)
          }
        }}
        onClick={(e: React.MouseEvent) => {
          if (disabled) {
            e.stopPropagation()
            return
          }
          if (onClick) {
            e.preventDefault()
            onClick(e)
          }
        }}
        {...rest}
      >
        {children}
      </ComponentProp>
    )
  },
)

export default ListItem

type ListItemChildProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>>

export const ListItemText = ({ children, className }: ListItemChildProps) => {
  const classes = useStyles()
  return <div className={clsx(classes.itemText, className)}>{children}</div>
}

export const ListItemIcon = ({ children, className }: ListItemChildProps) => {
  const classes = useStyles()
  return <div className={clsx(classes.itemIcon, className)}>{children}</div>
}

export const ListDivider = (props: ListItemChildProps) => {
  useDeprecatedComponent('ListDivider', 'Please use Divider')
  return <Divider {...props} />
}
